import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from 'src/app-redux/rtkq.interceptor';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import { apiResources } from 'src/common/Constants';
import { environment } from 'src/environments/environment';
import { Driver, ICreateDriverArgs } from 'src/management/Driver';
import { driversTag } from 'src/app-redux/rtkq.tags';

/** RTK Query API slice for driver management endpoints. */
export const driversApi = createApi({
  reducerPath: 'driversApi',
  tagTypes: [driversTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch drivers with pagination and search parameters. */
    getDrivers: builder.query<Driver[], { page: number; searchParam: { [id: string]: string } }>({
      providesTags: [driversTag],
      query: (args) => {
        return {
          url: apiResources.driver,
          params: { page: args.page, ...args.searchParam },
          method: 'GET',
        };
      },
      ...transformResponses,
    }),
    /** Query to find a driver by username. */
    findDriverByUsername: builder.query<Driver, string>({
      providesTags: [driversTag],
      query: (username) => `${apiResources.driver}/${username}`,
      ...transformResponses,
    }),
    /** Mutation to create a new driver. */
    createDriver: builder.mutation<Driver, ICreateDriverArgs>({
      query: (driver) => {
        return {
          url: apiResources.driver,
          method: 'POST',
          body: driver,
        };
      },
      invalidatesTags: (_result, error) => (error ? [] : [driversTag]),
    }),
    /** Mutation to update an existing driver. */
    updateDriver: builder.mutation<Driver, Driver>({
      query: (driver) => {
        return {
          url: apiResources.driver,
          method: 'PUT',
          body: driver,
        };
      },
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [driversTag]),
    }),
    /** Mutation to delete a driver by ID. */
    deleteDriver: builder.mutation<string, number>({
      query: (driverId) => ({
        url: `${apiResources.driver}/${driverId}`,
        method: 'DELETE',
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [driversTag]),
    }),
    /**
     * This API is used externally without authentication only in the delete driver account flow. DON"T USE IN NORMAL DELETE DRIVER FLOW.
     */
    deleteDriverAccount: builder.mutation<void, string>({
      query: (phoneNumber) => ({
        url: `${apiResources.driver}/delete-account/${phoneNumber}`,
        method: 'DELETE',
        body: { clientSecret: environment.VITE_CLIENT_SECRET_CSR },
      }),
    }),
  }),
});

/** RTK Query hooks for driver management endpoints. */
export const {
  useCreateDriverMutation,
  useGetDriversQuery,
  useLazyGetDriversQuery,
  useDeleteDriverAccountMutation,
  useLazyFindDriverByUsernameQuery,
  useDeleteDriverMutation,
  useUpdateDriverMutation,
} = driversApi;
